import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { Activity, ArrowLeft, FileText, Gauge, LayoutGrid, Layers, Sparkles, UploadCloud } from 'lucide-react';
import apiService, { apiActor } from '../../services/apiService';
import ForecastWaitingQueue from './ForecastWaitingQueue';
import ForecastBulkActionBar from './ForecastBulkActionBar';
import ForecastSavedViewsMenu from './ForecastSavedViewsMenu';
import {
  STATUS_LABELS,
  STATUS_TONES,
  formatMillions,
  formatPct,
  getActiveForecastUser
} from './forecastUsers';

const SLA_TONE = { breach: 'red', watch: 'yellow', healthy: 'green' };

const TABS = ['Status board', 'Trends', 'Consolidation'];
const FTP_RATE = 0.0565; // SOFR 4.85% + 0.80% transfer spread — mirrors backend

const GAAP_METRICS = [
  { key: 'loans', label: 'Loans' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'nii', label: 'NII' },
  { key: 'fees', label: 'Fees' },
  { key: 'expense', label: 'Expense', lowerBetter: true },
  { key: 'netIncome', label: 'Net income' }
];
const FTP_METRICS = [
  { key: 'loans', label: 'Loans' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'lendingMargin', label: 'Lending margin' },
  { key: 'depositFranchise', label: 'Deposit franchise' },
  { key: 'fees', label: 'Fees' },
  { key: 'expense', label: 'Expense', lowerBetter: true },
  { key: 'netIncome', label: 'Net income' }
];

// Run-rate (annualized 2026 actuals) vs prior cycle FY2026 plan, per metric —
// including the FTP decomposition of NII into lending margin and deposit franchise.
const computeGaps = (group) => {
  const actual2026 = (group.series.actuals || []).find((row) => row.period === '2026');
  const prior2026 = (group.series.prior || []).find((row) => row.period === '2026');
  if (!actual2026 || !prior2026) return null;
  const months = actual2026.months || 5;
  const annualize = (value) => (value / months) * 12;
  const gaps = {};
  ['loans', 'deposits'].forEach((key) => {
    gaps[key] = prior2026[key] ? ((actual2026[key] - prior2026[key]) / Math.abs(prior2026[key])) * 100 : null;
  });
  ['nii', 'fees', 'expense', 'netIncome'].forEach((key) => {
    gaps[key] = prior2026[key] ? ((annualize(actual2026[key]) - prior2026[key]) / Math.abs(prior2026[key])) * 100 : null;
  });
  const lmActual = annualize(actual2026.interestIncome) - FTP_RATE * actual2026.loans;
  const lmPrior = prior2026.interestIncome - FTP_RATE * prior2026.loans;
  const dfActual = FTP_RATE * actual2026.deposits - annualize(actual2026.interestExpense);
  const dfPrior = FTP_RATE * prior2026.deposits - prior2026.interestExpense;
  gaps.lendingMargin = lmPrior ? ((lmActual - lmPrior) / Math.abs(lmPrior)) * 100 : null;
  gaps.depositFranchise = dfPrior ? ((dfActual - dfPrior) / Math.abs(dfPrior)) * 100 : null;
  gaps.niDollarsMM = (annualize(actual2026.netIncome) - prior2026.netIncome) / 1e6;
  const driver = ['nii', 'fees', 'expense'].reduce(
    (best, key) =>
      gaps[key] !== null && (best === null || Math.abs(gaps[key]) > Math.abs(gaps[best])) ? key : best,
    null
  );
  return { gaps, driver, niGap: gaps.netIncome };
};

const gapTone = (value, lowerBetter = false) => {
  if (value === null || value === undefined) return 'na';
  const signed = lowerBetter ? -value : value;
  if (signed > 4) return 'pos2';
  if (signed > 1.5) return 'pos1';
  if (signed < -4) return 'neg2';
  if (signed < -1.5) return 'neg1';
  return 'flat';
};

const FLAG_TONES = { anomaly: 'red', watch: 'yellow', normal: 'green' };

// Gateway errors (e.g. Vercel 504s) return objects — never render those raw.
const errText = (err) => {
  const detail = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? err;
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const ForecastReviewConsole = () => {
  const navigate = useNavigate();
  const activeUser = getActiveForecastUser();
  const activeActor = apiActor(activeUser);
  const [tab, setTab] = useState(TABS[0]);
  const [basis, setBasis] = useState('ftp'); // FTP (internal) is the default reporting view
  const [data, setData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [companyComparison, setCompanyComparison] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [divisionFilter, setDivisionFilter] = useState('all');
  const [marketFilter, setMarketFilter] = useState('all');
  const [inputterFilter, setInputterFilter] = useState('all');
  const [spanOnly, setSpanOnly] = useState(true); // RBAC: manager sees their liaisons' units first
  const [loadingUnit, setLoadingUnit] = useState(null);
  const [loadResults, setLoadResults] = useState({});
  // Targeted AI variance scans: per-unit notes, one request in flight at a time.
  const [aiNotes, setAiNotes] = useState({});           // unitKey -> {status, flag, note}
  const [aiOverall, setAiOverall] = useState(null);
  const [autoScanned, setAutoScanned] = useState([]);   // unit keys picked by threshold
  const scanChainRef = useRef(Promise.resolve());
  const autoScanFiredRef = useRef(false);
  const [consolidation, setConsolidation] = useState(null);
  const [consolidationBusy, setConsolidationBusy] = useState(false);
  const [glLens, setGlLens] = useState(null);           // company-level GL-group comparison
  const [waitingQueue, setWaitingQueue] = useState(null); // B1: waiting-on-me SLA queue
  const [selectedIds, setSelectedIds] = useState([]); // B2: bulk selection
  const [refreshTick, setRefreshTick] = useState(0); // B2: re-pull after bulk action

  // B1: pull the waiting-on-me queue once for the count chip + per-row SLA map.
  // Server owns SLA aging and totals; the console only surfaces them.
  useEffect(() => {
    let cancelled = false;
    apiService.fetchForecastWaitingQueue({ cycleCode: 'FY2027_BUDGET', actor: activeUser.name })
      .then((result) => { if (!cancelled) setWaitingQueue(result); })
      .catch(() => { if (!cancelled) setWaitingQueue(null); });
    return () => { cancelled = true; };
  }, [activeUser.name]);

  const slaBySubmission = useMemo(() => {
    const lookup = {};
    (waitingQueue?.items || []).forEach((item) => { lookup[item.submissionId] = item; });
    return lookup;
  }, [waitingQueue]);

  useEffect(() => {
    Promise.all([
      apiService.fetchForecastSubmissions('FY2027_BUDGET'),
      apiService.fetchForecastComparison({ cycleCode: 'FY2027_BUDGET', level: 'entity', grain: 'annual' }),
      apiService.fetchForecastComparison({ cycleCode: 'FY2027_BUDGET', level: 'company', grain: 'annual' })
    ])
      .then(([submissions, entityComparison, company]) => {
        setData(submissions);
        setComparison(entityComparison);
        setCompanyComparison(company);
      })
      .catch((err) => setError(errText(err)));
  }, [refreshTick]);

  const submissionByEntity = useMemo(() => {
    const lookup = {};
    (data?.submissions || []).forEach((submission) => {
      lookup[submission.planningEntity] = submission;
    });
    return lookup;
  }, [data]);

  const managesSomeone = useMemo(
    () => (data?.submissions || []).some((submission) => submission.fpaManager === activeUser.name),
    [data, activeUser.name]
  );

  // Shared scope filter: RBAC span + division + market + inputter.
  const inScope = (submission) => {
    if (!submission) return false;
    if (spanOnly && managesSomeone && submission.fpaManager !== activeUser.name) return false;
    if (divisionFilter !== 'all' && submission.division !== divisionFilter) return false;
    if (marketFilter !== 'all' && submission.market !== marketFilter) return false;
    if (inputterFilter !== 'all' && (submission.liaison || submission.assignedTo) !== inputterFilter) return false;
    return true;
  };

  const gapsByEntity = useMemo(() => {
    const lookup = {};
    (comparison?.groups || []).forEach((group) => {
      lookup[group.key] = computeGaps(group);
    });
    return lookup;
  }, [comparison]);

  const divisions = useMemo(
    () => [...new Set((data?.submissions || []).map((submission) => submission.division))].sort(),
    [data]
  );
  const markets = useMemo(
    () => [...new Set((data?.submissions || [])
      .filter((submission) => divisionFilter === 'all' || submission.division === divisionFilter)
      .map((submission) => submission.market))].sort(),
    [data, divisionFilter]
  );
  const inputters = useMemo(
    () => [...new Set((data?.submissions || []).map((submission) => submission.liaison || submission.assignedTo)
      .filter((name) => name && name !== 'Unassigned'))].sort(),
    [data]
  );

  const rows = useMemo(() => {
    const submissions = data?.submissions || [];
    const statusPriority = { submitted: 0, returned: 1, in_progress: 2, approved: 3, loaded: 4, not_started: 5 };
    return submissions
      .filter((submission) => statusFilter === 'all' || submission.status === statusFilter)
      .filter(inScope)
      .map((submission) => {
        const fy1 = submission.modelSnapshot?.annualResults?.[0] || null;
        const gapInfo = gapsByEntity[submission.planningEntity] || null;
        return { ...submission, fy1, gapInfo };
      })
      .sort((a, b) => {
        const pa = statusPriority[a.status] ?? 9;
        const pb = statusPriority[b.status] ?? 9;
        if (pa !== pb) return pa - pb;
        const ga = Math.abs(a.gapInfo?.niGap ?? 0);
        const gb = Math.abs(b.gapInfo?.niGap ?? 0);
        return gb - ga;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, statusFilter, divisionFilter, marketFilter, inputterFilter, spanOnly, gapsByEntity]);

  // B2: current console filter state as a saveable/restorable view object.
  const currentFilters = useMemo(
    () => ({ status: statusFilter, division: divisionFilter, market: marketFilter, inputter: inputterFilter }),
    [statusFilter, divisionFilter, marketFilter, inputterFilter]
  );

  const applySavedView = (filters) => {
    if (!filters) return;
    setStatusFilter(filters.status || 'all');
    setDivisionFilter(filters.division || 'all');
    setMarketFilter(filters.market || 'all');
    setInputterFilter(filters.inputter || 'all');
  };

  // B2: rows eligible for a bulk review action (submitted awaiting review).
  const bulkEligibleIds = useMemo(
    () => rows.filter((r) => r.status === 'submitted').map((r) => r.id),
    [rows]
  );
  const selectableSet = useMemo(() => new Set(bulkEligibleIds), [bulkEligibleIds]);
  // Keep selection within the currently visible eligible set.
  const effectiveSelected = useMemo(
    () => selectedIds.filter((id) => selectableSet.has(id)),
    [selectedIds, selectableSet]
  );
  const toggleRow = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () =>
    setSelectedIds((prev) => (effectiveSelected.length === bulkEligibleIds.length ? [] : bulkEligibleIds));

  const heatmapMetrics = basis === 'ftp' ? FTP_METRICS : GAAP_METRICS;

  const trendGroups = useMemo(
    () => (comparison?.groups || []).filter((group) => inScope(submissionByEntity[group.key])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comparison, submissionByEntity, divisionFilter, marketFilter, inputterFilter, spanOnly, data]
  );

  const scanBatch = async (unitKeys, provider, attempt = 0) => {
    try {
      const result = await apiService.fetchVarianceReview({
        cycleCode: 'FY2027_BUDGET',
        scope: 'trends',
        entities: unitKeys,
        provider
      });
      const byUnit = Object.fromEntries((result.rows || []).map((row) => [row.unit, row]));
      setAiNotes((previous) => ({
        ...previous,
        ...Object.fromEntries(unitKeys.map((key) => [
          key,
          byUnit[key]
            ? { status: 'done', flag: byUnit[key].flag, note: byUnit[key].note, model: result.model }
            : { status: 'error', note: errText(result.detail || 'no note returned') }
        ]))
      }));
      if (result.overall && unitKeys.length > 1) setAiOverall(result.overall);
    } catch (err) {
      // Gateway hiccups (Vercel 5xx, cold starts) get one automatic retry.
      if (attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        return scanBatch(unitKeys, provider, 1);
      }
      const message = errText(err);
      setAiNotes((previous) => ({
        ...previous,
        ...Object.fromEntries(unitKeys.map((key) => [key, { status: 'error', note: message }]))
      }));
    }
    return undefined;
  };

  // Manual / auto scans: queue behind one another against the Anthropic key
  // (with server-side OpenRouter fallback if Anthropic errors).
  const scanUnits = (unitKeys) => {
    const fresh = unitKeys.filter((key) => !aiNotes[key] || aiNotes[key].status === 'error');
    if (fresh.length === 0) return;
    setAiNotes((previous) => ({
      ...previous,
      ...Object.fromEntries(fresh.map((key) => [key, { status: 'pending' }]))
    }));
    scanChainRef.current = scanChainRef.current.then(() => scanBatch(fresh, 'auto'));
  };

  // Swarm: blanket the visible rows with parallel single-unit scans on the
  // cheap OpenRouter models (MiMo / Kimi), capped at 6 concurrent requests.
  const [swarming, setSwarming] = useState(false);
  const swarmScan = async (unitKeys) => {
    const fresh = unitKeys.filter((key) => !aiNotes[key] || aiNotes[key].status === 'error');
    if (fresh.length === 0 || swarming) return;
    setSwarming(true);
    setAiNotes((previous) => ({
      ...previous,
      ...Object.fromEntries(fresh.map((key) => [key, { status: 'pending' }]))
    }));
    const queue = [...fresh];
    const worker = async () => {
      while (queue.length > 0) {
        const key = queue.shift();
        // eslint-disable-next-line no-await-in-loop
        await scanBatch([key], 'openrouter');
      }
    };
    // Three concurrent workers: parallel enough to feel instant on a division,
    // gentle enough that serverless cold starts and the DB pooler keep up.
    await Promise.all(Array.from({ length: Math.min(3, fresh.length) }, worker));
    setSwarming(false);
  };

  // Auto-kick: when the matrix lands, scan the three biggest movers breaching
  // a 5% gap on any P&L column — one batched call, no manual step needed.
  useEffect(() => {
    if (autoScanFiredRef.current) return;
    if (!comparison || !data) return;
    const candidates = (comparison.groups || [])
      .filter((group) => inScope(submissionByEntity[group.key]))
      .map((group) => {
        const gaps = gapsByEntity[group.key]?.gaps;
        if (!gaps) return null;
        const score = Math.max(
          ...['nii', 'fees', 'expense', 'netIncome'].map((key) => Math.abs(gaps[key] ?? 0))
        );
        return { key: group.key, score };
      })
      .filter((candidate) => candidate && candidate.score >= 5)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((candidate) => candidate.key);
    autoScanFiredRef.current = true;
    if (candidates.length > 0) {
      setAutoScanned(candidates);
      scanUnits(candidates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparison, data, gapsByEntity]);

  // Consolidated AI flash: auto-runs on tab open, retries one gateway hiccup.
  const runConsolidationFlash = async (attempt = 0) => {
    setConsolidationBusy(true);
    try {
      const result = await apiService.fetchVarianceReview({ cycleCode: 'FY2027_BUDGET', scope: 'consolidation' });
      setConsolidation(result);
    } catch (err) {
      if (attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        return runConsolidationFlash(1);
      }
      setConsolidation({ status: 'error', detail: errText(err) });
    } finally {
      setConsolidationBusy(false);
    }
    return undefined;
  };

  useEffect(() => {
    if (tab !== 'Consolidation') return;
    if (!consolidation && !consolidationBusy) {
      runConsolidationFlash();
    }
    if (!glLens) {
      apiService.fetchForecastComparison({
        cycleCode: 'FY2027_BUDGET', level: 'company', grain: 'annual', detail: 'glGroup'
      })
        .then(setGlLens)
        .catch(() => setGlLens({ groups: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // GL-group lens: 2026 run-rate vs prior plan, per Major rollup-2 bucket.
  const glLensRows = useMemo(() => {
    if (!glLens?.groups) return null;
    const rows = [];
    glLens.groups.forEach((group) => {
      const actual = (group.series.actuals || []).find((row) => row.period === '2026');
      const prior = (group.series.prior || []).find((row) => row.period === '2026');
      if (!actual || !prior || !prior.value) return;
      const isBalance = group.measure === 'EOP';
      const months = actual.months || 5;
      const actualAnnual = isBalance ? actual.value : (actual.value / months) * 12;
      rows.push({
        glGroup: group.glGroup,
        rollup: group.rollup,
        gapMM: (actualAnnual - prior.value) / 1e6,
        gapPct: ((actualAnnual - prior.value) / Math.abs(prior.value)) * 100
      });
    });
    return {
      nir: rows.filter((row) => row.rollup === 'Non-Interest Income').sort((a, b) => a.gapMM - b.gapMM),
      nie: rows.filter((row) => row.rollup === 'Non-Interest Expense').sort((a, b) => b.gapMM - a.gapMM)
    };
  }, [glLens]);

  // Inputter (uploader) lens: net-income gap dollars accumulated per liaison.
  const inputterLens = useMemo(() => {
    const byLiaison = {};
    (data?.submissions || []).forEach((submission) => {
      const liaison = submission.liaison || submission.assignedTo;
      if (!liaison || liaison === 'Unassigned') return;
      const dollars = gapsByEntity[submission.planningEntity]?.gaps?.niDollarsMM;
      if (dollars === undefined || dollars === null) return;
      const bucket = byLiaison[liaison] = byLiaison[liaison] || { liaison, units: 0, gapMM: 0 };
      bucket.units += 1;
      bucket.gapMM += dollars;
    });
    return Object.values(byLiaison).sort((a, b) => a.gapMM - b.gapMM);
  }, [data, gapsByEntity]);

  const counts = data?.statusCounts || {};
  const scope = data?.scope || null;
  const total = scope?.total ?? (data?.submissions?.length || 0);
  const awaitingReview = scope?.awaitingReview ?? (counts.submitted || 0);
  const approvedOrLoaded = scope?.approvedOrLoaded ?? ((counts.approved || 0) + (counts.loaded || 0));
  const returned = scope?.returned ?? (counts.returned || 0);

  // Cycle pulse: server scope only when showing the full cycle; filtered views use the visible scope.
  const pulse = useMemo(() => {
    const scoped = (data?.submissions || []).filter(inScope);
    const showingFullCycle =
      !spanOnly && divisionFilter === 'all' && marketFilter === 'all' && inputterFilter === 'all';
    const isDone = (submission) => ['submitted', 'approved', 'loaded'].includes(submission.status);
    const fallbackOverall = {
      total: scoped.length,
      done: scoped.filter(isDone).length,
      approved: scoped.filter((submission) => ['approved', 'loaded'].includes(submission.status)).length,
      inFlight: scoped.filter((submission) => ['in_progress', 'returned'].includes(submission.status)).length,
      untouched: scoped.filter((submission) => submission.status === 'not_started').length
    };
    const overall = data?.scope && showingFullCycle
      ? {
          total: data.scope.total || 0,
          done: data.scope.submittedOrBeyond || 0,
          approved: data.scope.approvedOrLoaded || 0,
          inFlight: (data.scope.inProgress || 0) + (data.scope.returned || 0),
          untouched: data.scope.notStarted || 0
        }
      : fallbackOverall;
    const byInputter = {};
    scoped.forEach((submission) => {
      const name = submission.liaison || submission.assignedTo;
      if (!name || name === 'Unassigned') return;
      const bucket = byInputter[name] = byInputter[name] || { name, total: 0, done: 0, inFlight: 0 };
      bucket.total += 1;
      if (isDone(submission)) bucket.done += 1;
      else if (submission.status !== 'not_started') bucket.inFlight += 1;
    });
    return {
      overall,
      inputters: Object.values(byInputter).sort((a, b) => (b.done / b.total) - (a.done / a.total))
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, divisionFilter, marketFilter, inputterFilter, spanOnly]);

  const approvedRows = useMemo(
    () => (data?.submissions || []).filter((submission) => ['approved', 'loaded'].includes(submission.status)),
    [data]
  );

  const companyKpis = useMemo(() => {
    const group = companyComparison?.groups?.[0];
    if (!group) return null;
    const actual = (group.series.actuals || []).find((row) => row.period === '2026');
    const prior = (group.series.prior || []).find((row) => row.period === '2026');
    if (!actual || !prior) return null;
    const months = actual.months || 5;
    const annualize = (value) => (value / months) * 12;
    const gapPct = (actualValue, priorValue) =>
      priorValue ? ((actualValue - priorValue) / Math.abs(priorValue)) * 100 : null;
    const kpis = [
      { label: 'Net income', value: annualize(actual.netIncome) / 1e6, gap: gapPct(annualize(actual.netIncome), prior.netIncome) }
    ];
    if (basis === 'ftp') {
      const lm = annualize(actual.interestIncome) - FTP_RATE * actual.loans;
      const lmPrior = prior.interestIncome - FTP_RATE * prior.loans;
      const df = FTP_RATE * actual.deposits - annualize(actual.interestExpense);
      const dfPrior = FTP_RATE * prior.deposits - prior.interestExpense;
      kpis.push(
        { label: 'Lending margin', value: lm / 1e6, gap: gapPct(lm, lmPrior) },
        { label: 'Deposit franchise', value: df / 1e6, gap: gapPct(df, dfPrior) }
      );
    } else {
      kpis.push({ label: 'NII', value: annualize(actual.nii) / 1e6, gap: gapPct(annualize(actual.nii), prior.nii) });
    }
    kpis.push(
      { label: 'Fees', value: annualize(actual.fees) / 1e6, gap: gapPct(annualize(actual.fees), prior.fees) },
      { label: 'Expense', value: annualize(actual.expense) / 1e6, gap: gapPct(annualize(actual.expense), prior.expense), lowerBetter: true }
    );
    return kpis;
  }, [companyComparison, basis]);

  const companyAnnual = useMemo(() => {
    const group = companyComparison?.groups?.[0];
    if (!group) return [];
    const priorByYear = Object.fromEntries((group.series.prior || []).map((row) => [row.period, row]));
    const budgetByYear = Object.fromEntries((group.series.budget || []).map((row) => [row.period, row]));
    const actualByYear = Object.fromEntries((group.series.actuals || []).map((row) => [row.period, row]));
    const years = [...new Set([
      ...Object.keys(priorByYear), ...Object.keys(budgetByYear), ...Object.keys(actualByYear)
    ])].sort();
    return years.map((year) => ({
      year,
      actualNi: actualByYear[year]?.netIncome ?? null,
      actualMonths: actualByYear[year]?.months ?? null,
      priorNi: priorByYear[year]?.netIncome ?? null,
      budgetNi: budgetByYear[year]?.netIncome ?? null,
      actualLoans: actualByYear[year]?.loans ?? null,
      priorLoans: priorByYear[year]?.loans ?? null,
      actualDeposits: actualByYear[year]?.deposits ?? null,
      priorDeposits: priorByYear[year]?.deposits ?? null
    }));
  }, [companyComparison]);

  const writeApprovedUnit = async (submission) => {
    setLoadingUnit(submission.id);
    try {
      const record = await apiService.fetchForecastSubmission(submission.id);
      const inputs = record.inputs || {};
      if (!Array.isArray(inputs.loadRows) || inputs.loadRows.length === 0) {
        throw new Error('No staged cube rows were saved with this package.');
      }
      const result = await apiService.loadRegionalForecast({
        scenarioVersion: record.model_snapshot?.scenarioVersion
          || `${record.cycle_code}_${record.planning_entity}`,
        submissionId: submission.id,
        versionRaw: 'RFM',
        planningEntity: record.planning_entity,
        market: record.market,
        region: record.division,
        dataSource: 'RegionalForecastModule',
        createdBy: activeActor,
        rows: inputs.loadRows,
        summaryRows: inputs.stagedRows || [],
        metadata: {
          cycleCode: record.cycle_code,
          submittedBy: record.submitted_by,
          approvedBy: record.reviewed_by,
          loadedBy: activeActor,
          narratives: record.narratives || []
        }
      });
      await apiService.transitionForecastSubmission(submission.id, {
        action: 'load_complete',
        actor: activeActor,
        payload: { loadBatchId: result.loadBatchId, insertedRows: result.insertedRows }
      });
      setLoadResults((previous) => ({ ...previous, [submission.id]: result }));
      const refreshed = await apiService.fetchForecastSubmissions('FY2027_BUDGET');
      setData(refreshed);
    } catch (err) {
      setLoadResults((previous) => ({
        ...previous,
        [submission.id]: { status: 'error', message: errText(err) }
      }));
    } finally {
      setLoadingUnit(null);
    }
  };

  const firstComment = (submission) =>
    (submission?.narratives || []).map((narrative) => narrative.text).find((text) => (text || '').trim()) || null;

  return (
    <div className="forecast-module forecast-review">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <Activity size={14} />
            Forecast Console / FP&A review
            <Link to="/forecasting" className="forecast-kicker-back">
              <ArrowLeft size={12} /> Cycle home
            </Link>
          </div>
          <h1>Review console</h1>
          <p>
            {data?.cycle?.name || 'Cycle'} · {total ? `${awaitingReview} awaiting review · ${approvedOrLoaded} approved/loaded · ${returned} returned` : 'Loading…'}
          </p>
        </div>
        <div className="forecast-command-grid review-filters">
          <label>
            Status
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">All statuses</option>
              {Object.keys(STATUS_LABELS).map((status) => (
                <option value={status} key={status}>{STATUS_LABELS[status]}</option>
              ))}
            </select>
          </label>
          <label>
            Division
            <select value={divisionFilter} onChange={(event) => { setDivisionFilter(event.target.value); setMarketFilter('all'); }}>
              <option value="all">All divisions</option>
              {divisions.map((division) => (
                <option value={division} key={division}>{division}</option>
              ))}
            </select>
          </label>
          <label>
            Market
            <select value={marketFilter} onChange={(event) => setMarketFilter(event.target.value)}>
              <option value="all">All markets</option>
              {markets.map((market) => (
                <option value={market} key={market}>{market}</option>
              ))}
            </select>
          </label>
          <label>
            Inputter
            <select value={inputterFilter} onChange={(event) => setInputterFilter(event.target.value)}>
              <option value="all">All inputters</option>
              {inputters.map((name) => (
                <option value={name} key={name}>{name}</option>
              ))}
            </select>
          </label>
          <ForecastSavedViewsMenu
            actor={activeUser.name}
            surface="review_console"
            currentFilters={currentFilters}
            onApply={applySavedView}
          />
        </div>
      </section>

      <section className="forecast-operating-bar">
        <div className="forecast-section-nav">
          {TABS.map((candidate) => (
            <button
              type="button"
              key={candidate}
              className={tab === candidate ? 'active' : ''}
              onClick={() => setTab(candidate)}
            >
              {candidate === 'Status board' && <LayoutGrid size={13} style={{ marginRight: 4 }} />}
              {candidate === 'Trends' && <Gauge size={13} style={{ marginRight: 4 }} />}
              {candidate === 'Consolidation' && <Layers size={13} style={{ marginRight: 4 }} />}
              {candidate}
            </button>
          ))}
        </div>
        <div className="forecast-health-strip">
          <span className="forecast-segmented basis-tabs">
            <button type="button" className={basis === 'ftp' ? 'active' : ''} onClick={() => setBasis('ftp')}>
              FTP · Internal
            </button>
            <button type="button" className={basis === 'gaap' ? 'active' : ''} onClick={() => setBasis('gaap')}>
              GAAP
            </button>
          </span>
          {managesSomeone && (
            <button
              type="button"
              className={`forecast-health-chip ${spanOnly ? 'gold' : 'neutral'} span-toggle`}
              onClick={() => setSpanOnly((previous) => !previous)}
            >
              {spanOnly ? `My span — ${activeUser.name}` : 'All units'}
            </button>
          )}
          <Link to="/forecasting/statements?level=company" className="forecast-health-chip neutral statement-view-chip">
            <FileText size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />
            Statement view
          </Link>
          <span className="forecast-health-chip neutral">{rows.length} in scope</span>
          <span className="forecast-health-chip gold">{awaitingReview} to review</span>
          <span className="forecast-health-chip red">{returned} returned</span>
          {waitingQueue?.counts?.total > 0 && (
            <span className={`forecast-health-chip ${waitingQueue.counts.breached > 0 ? 'red' : waitingQueue.counts.watch > 0 ? 'yellow' : 'neutral'}`}>
              {waitingQueue.counts.total} waiting on me
            </span>
          )}
        </div>
      </section>

      {error && <div className="forecast-panel forecast-home-error"><p>{error}</p></div>}

      {tab === 'Status board' && (
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">RV_00 ● Cycle pulse</span>
            <h2>
              {pulse.overall.total
                ? `${pulse.overall.done} of ${pulse.overall.total} packages in — ${Math.round((pulse.overall.done / pulse.overall.total) * 100)}% of the scope is submitted or beyond`
                : 'Cycle pulse'}
            </h2>
          </div>
          <div className="pulse-grid">
            <div className="pulse-card">
              <span>Submitted+</span>
              <strong>{pulse.overall.done}<em>/{pulse.overall.total}</em></strong>
              <span
                className="pulse-track"
                title={`${pulse.overall.done} of ${pulse.overall.total} packages submitted, approved, or loaded (${pulse.overall.total ? Math.round((pulse.overall.done / pulse.overall.total) * 100) : 0}%)`}
              >
                <span className="pulse-fill moss" style={{ width: `${pulse.overall.total ? (pulse.overall.done / pulse.overall.total) * 100 : 0}%` }} />
              </span>
            </div>
            <div className="pulse-card">
              <span>Approved / loaded</span>
              <strong>{pulse.overall.approved}<em>/{pulse.overall.total}</em></strong>
              <span
                className="pulse-track"
                title={`${pulse.overall.approved} of ${pulse.overall.total} packages approved by FP&A or loaded to the cube (${pulse.overall.total ? Math.round((pulse.overall.approved / pulse.overall.total) * 100) : 0}%)`}
              >
                <span className="pulse-fill gold" style={{ width: `${pulse.overall.total ? (pulse.overall.approved / pulse.overall.total) * 100 : 0}%` }} />
              </span>
            </div>
            <div className="pulse-card">
              <span>In flight (working / returned)</span>
              <strong>{pulse.overall.inFlight}</strong>
              <span
                className="pulse-track"
                title={`${pulse.overall.inFlight} packages being worked or returned for rework — drafts exist but nothing is submitted yet`}
              >
                <span className="pulse-fill amber" style={{ width: `${pulse.overall.total ? (pulse.overall.inFlight / pulse.overall.total) * 100 : 0}%` }} />
              </span>
            </div>
            <div className="pulse-card">
              <span>Not started</span>
              <strong className={pulse.overall.untouched > 0 ? 'neg' : ''}>{pulse.overall.untouched}</strong>
              <span
                className="pulse-track"
                title={`${pulse.overall.untouched} packages untouched — no draft saved in this cycle yet`}
              >
                <span className="pulse-fill red" style={{ width: `${pulse.overall.total ? (pulse.overall.untouched / pulse.overall.total) * 100 : 0}%` }} />
              </span>
            </div>
          </div>
          {pulse.inputters.length > 0 && (
            <div className="pulse-inputters">
              {pulse.inputters.map((inputter) => (
                <div className="pulse-inputter-row" key={inputter.name}>
                  <span className="pulse-inputter-name">{inputter.name}</span>
                  <span
                    className="pulse-track wide"
                    title={`${inputter.name}: ${inputter.done} submitted+ (green), ${inputter.inFlight} in progress (amber), ${inputter.total - inputter.done - inputter.inFlight} not started of ${inputter.total} units`}
                  >
                    <span className="pulse-fill moss" style={{ width: `${(inputter.done / inputter.total) * 100}%` }} />
                    <span className="pulse-fill amber" style={{ width: `${(inputter.inFlight / inputter.total) * 100}%` }} />
                  </span>
                  <span className="forecast-mono pulse-inputter-count">
                    {inputter.done}/{inputter.total}
                    {inputter.inFlight > 0 ? ` · ${inputter.inFlight} working` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tab === 'Status board' && (
        <ForecastWaitingQueue cycleCode="FY2027_BUDGET" actor={activeUser.name} />
      )}

      {tab === 'Status board' && effectiveSelected.length > 0 && (
        <ForecastBulkActionBar
          cycleCode="FY2027_BUDGET"
          actor={activeActor}
          selectedIds={effectiveSelected}
          onClear={() => setSelectedIds([])}
          onDone={() => { setSelectedIds([]); setRefreshTick((t) => t + 1); }}
        />
      )}

      {tab === 'Status board' && (
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">RV_01 ● Status board</span>
            <h2>Sub-business packages, ranked by attention needed</h2>
          </div>
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table forecast-review-table">
              <thead>
                <tr>
                  <th className="forecast-select-col">
                    {bulkEligibleIds.length > 0 && (
                      <input
                        type="checkbox"
                        aria-label="Select all reviewable"
                        checked={effectiveSelected.length === bulkEligibleIds.length}
                        onChange={toggleAll}
                      />
                    )}
                  </th>
                  <th>Unit</th>
                  <th>Inputter</th>
                  <th>Status</th>
                  <th>Aging</th>
                  <th>FY1 Δ vs baseline</th>
                  <th>2026 run-rate vs prior plan</th>
                  <th>Driver</th>
                  <th>Constraints</th>
                  <th>Narrative</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const constraintSummary = row.modelSnapshot?.constraintSummary;
                  const sla = slaBySubmission[row.id];
                  const selectable = row.status === 'submitted';
                  return (
                    <tr key={row.id} className={effectiveSelected.includes(row.id) ? 'row-selected' : ''}>
                      <td className="forecast-select-col">
                        {selectable && (
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.division} ${row.market}`}
                            checked={effectiveSelected.includes(row.id)}
                            onChange={() => toggleRow(row.id)}
                          />
                        )}
                      </td>
                      <td>
                        <strong>{row.division}</strong>
                        <span className="forecast-home-market"> · {row.market}</span>
                      </td>
                      <td>{row.liaison || row.assignedTo || '—'}</td>
                      <td>
                        <span className={`forecast-status-chip ${STATUS_TONES[row.status]}`}>
                          {STATUS_LABELS[row.status]}
                        </span>
                      </td>
                      <td>
                        {sla ? (
                          <span className={`forecast-health-chip ${SLA_TONE[sla.slaState] || 'neutral'} mini`}>
                            {sla.ageBusinessDays ?? '—'}{sla.slaDays ? `/${sla.slaDays}` : ''}d
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {row.fy1 ? (
                          <span className={row.fy1.delta >= 0 ? 'pos' : 'neg'}>
                            {formatMillions(row.fy1.delta)} ({formatPct(row.fy1.variancePct)})
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {row.gapInfo?.niGap !== null && row.gapInfo?.niGap !== undefined ? (
                          <span className={row.gapInfo.niGap >= 0 ? 'pos' : 'neg'}>
                            {formatPct(row.gapInfo.niGap)}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {row.gapInfo?.driver ? (
                          <span className="forecast-driver-tag">{GAAP_METRICS.find((metric) => metric.key === row.gapInfo.driver)?.label}</span>
                        ) : '—'}
                      </td>
                      <td>
                        {constraintSummary ? (
                          <span className={`forecast-health-chip ${constraintSummary.red ? 'red' : constraintSummary.yellow ? 'yellow' : 'green'}`}>
                            {constraintSummary.green}/{constraintSummary.yellow}/{constraintSummary.red}
                          </span>
                        ) : '—'}
                      </td>
                      <td>{(row.narratives || []).length > 0 ? '✓' : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="forecast-home-action"
                          onClick={() => navigate(`/forecasting/review/${row.id}`)}
                        >
                          Review
                        </button>
                        <Link
                          to={`/forecasting/statements?level=entity&entity=${row.planningEntity}`}
                          className="forecast-home-action ghost"
                        >
                          Statements
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Trends' && (
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">RV_02 ● Cross-unit trends</span>
            <h2>2026 run-rate vs prior cycle plan — systematic shifts light up as columns</h2>
          </div>
          <div className="forecast-submit-chips" style={{ marginBottom: 10 }}>
            <button
              type="button"
              className="forecast-primary-button compact"
              disabled={swarming}
              onClick={() => swarmScan(trendGroups.map((group) => group.key))}
            >
              ⚡ {swarming ? 'Swarm running…' : `Swarm scan ${trendGroups.length} visible rows`}
            </button>
            <span className="forecast-health-chip gold">
              <Sparkles size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />
              Auto-scan reads the top {autoScanned.length || 3} movers ±5% — ✦ scans one row · swarm fans out cheap parallel agents
            </span>
            <span className="forecast-health-chip moss">
              {Object.values(aiNotes).filter((note) => note.status === 'done').length} units read
              {Object.values(aiNotes).some((note) => note.status === 'pending') ? ' · scanning…' : ''}
            </span>
          </div>
          {aiOverall && (
            <p className="forecast-submit-note ai-overall" style={{ marginBottom: 12 }}>
              <Sparkles size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} />
              <strong>Systematic read:</strong> {aiOverall}
            </p>
          )}
          <div className="forecast-home-table-wrap">
            <table className="forecast-heatmap with-notes">
              <thead>
                <tr>
                  <th>Unit</th>
                  {heatmapMetrics.map((metric) => (
                    <th key={metric.key}>{metric.label}</th>
                  ))}
                  <th className="note-col">Inputter comment</th>
                  <th className="note-col">AI variance read</th>
                </tr>
              </thead>
              <tbody>
                {trendGroups.map((group) => {
                  const info = gapsByEntity[group.key];
                  const submission = submissionByEntity[group.key];
                  const aiNote = aiNotes[group.key];
                  return (
                    <tr key={group.key}>
                      <td>
                        <strong>{group.division}</strong>
                        <span className="forecast-home-market"> · {group.market}</span>
                      </td>
                      {heatmapMetrics.map((metric) => {
                        const value = info?.gaps?.[metric.key];
                        return (
                          <td key={metric.key} className={`heat ${gapTone(value, metric.lowerBetter)}`}>
                            {value !== null && value !== undefined ? formatPct(value) : '—'}
                          </td>
                        );
                      })}
                      <td className="note-col comment-cell">
                        {firstComment(submission) || <span className="dim-note">—</span>}
                      </td>
                      <td className="note-col ai-cell">
                        {aiNote?.status === 'done' ? (
                          <>
                            <span className={`forecast-health-chip ${FLAG_TONES[aiNote.flag]} mini`}>{aiNote.flag}</span>
                            <span>{aiNote.note}</span>
                          </>
                        ) : aiNote?.status === 'pending' ? (
                          <span className="dim-note">scanning…</span>
                        ) : aiNote?.status === 'error' ? (
                          <button type="button" className="ai-row-scan" onClick={() => scanUnits([group.key])}>
                            ✦ retry — {aiNote.note}
                          </button>
                        ) : (
                          <button type="button" className="ai-row-scan" onClick={() => scanUnits([group.key])}>
                            ✦ Scan
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="forecast-submit-note">
            Reading guide: each cell compares annualized 2026 management actuals against the
            FCST_2026Q1_PRIOR plan for the same unit. {basis === 'ftp'
              ? 'FTP view splits NII into lending margin (II − transfer charge on loans) and deposit franchise (transfer credit on deposits − IE) at SOFR + 0.80%.'
              : 'GAAP view shows NII as reported.'} A column that lights up across many rows is a
            systematic assumption shift. Expense cells invert color (overrun = red).
          </p>
        </section>
      )}

      {tab === 'Consolidation' && (
        <>
          <section className="forecast-panel consol-headline">
            <div className="forecast-panel-head">
              <span className="forecast-section-code">RV_03 ● The consolidated story</span>
              <h2>
                {consolidation?.status === 'ok'
                  ? consolidation.headline
                  : consolidationBusy
                    ? 'Reading the consolidated variances…'
                    : 'Total company vs prior-cycle plan'}
              </h2>
            </div>
            {consolidation?.status === 'ok' && (
              <span className="forecast-health-chip moss">AI flash · Haiku + variance-analysis method</span>
            )}
            {consolidation?.status === 'error' && (
              <span className="forecast-submit-chips">
                <span className="forecast-health-chip red">
                  AI summary unavailable: {String(consolidation.detail || 'unknown error')}
                </span>
                <button
                  type="button"
                  className="forecast-secondary-button compact"
                  disabled={consolidationBusy}
                  onClick={() => runConsolidationFlash(1)}
                >
                  {consolidationBusy ? 'Retrying…' : 'Retry'}
                </button>
              </span>
            )}

            {companyKpis && (
              <div className="consol-kpis">
                {companyKpis.map((kpi) => {
                  const tone = gapTone(kpi.gap, kpi.lowerBetter);
                  const toneClass = tone.startsWith('pos') ? 'pos' : tone.startsWith('neg') ? 'neg' : '';
                  return (
                    <div className="consol-kpi" key={kpi.label}>
                      <span>{kpi.label} · 2026 run-rate</span>
                      <strong>${kpi.value.toFixed(0)}MM</strong>
                      <em className={toneClass}>
                        {kpi.gap !== null ? `${formatPct(kpi.gap)} vs plan` : '—'}
                      </em>
                    </div>
                  );
                })}
              </div>
            )}

            {consolidation?.status === 'ok' && (
              <div className="consol-narrative">
                <div>
                  <h4>What's driving it</h4>
                  <ul>{consolidation.keyPoints.map((point) => <li key={point}>{point}</li>)}</ul>
                </div>
                {consolidation.anomalies?.length > 0 && (
                  <div>
                    <h4>Anomalies worth a look</h4>
                    <ul>{consolidation.anomalies.map((item) => <li key={item}>{item}</li>)}</ul>
                  </div>
                )}
                <div>
                  <h4>FY2027 budget implication</h4>
                  <p>{consolidation.outlook}</p>
                  {consolidation.decisionsNeeded?.length > 0 && (
                    <ul className="consol-decisions">
                      {consolidation.decisionsNeeded.map((item) => <li key={item}>□ {item}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </section>

          {consolidation?.divisionRows?.length > 0 && (
            <section className="forecast-panel">
              <div className="forecast-panel-head">
                <span className="forecast-section-code">RV_03B ● Where the gap sits</span>
                <h2>Division contribution to the run-rate vs plan gap ($MM annualized)</h2>
              </div>
              <div className="consol-bridge">
                {(() => {
                  const maxAbs = Math.max(...consolidation.divisionRows.map((row) => Math.abs(row.niGapMM)), 0.1);
                  return [...consolidation.divisionRows]
                    .sort((a, b) => b.niGapMM - a.niGapMM)
                    .map((row) => (
                      <div className="consol-bridge-row" key={row.division}>
                        <span className="consol-bridge-label">{row.division}</span>
                        <span className="consol-bridge-track">
                          <span
                            className={`consol-bridge-bar ${row.niGapMM >= 0 ? 'pos' : 'neg'}`}
                            style={{ width: `${(Math.abs(row.niGapMM) / maxAbs) * 100}%` }}
                          />
                        </span>
                        <span className={`forecast-mono ${row.niGapMM >= 0 ? 'pos' : 'neg'}`}>
                          {row.niGapMM >= 0 ? '+' : ''}{row.niGapMM.toFixed(1)}
                        </span>
                        <span className="consol-bridge-detail">
                          NII {formatPct(row.niiGapPct)} · fees {formatPct(row.feeGapPct)} · exp {formatPct(row.expenseGapPct)}
                        </span>
                      </div>
                    ));
                })()}
              </div>
            </section>
          )}

          {consolidation?.topUnitMovers?.length > 0 && (
            <section className="forecast-panel">
              <div className="forecast-panel-head">
                <span className="forecast-section-code">RV_03C ● Top movers</span>
                <h2>The eight units moving the consolidated number most</h2>
              </div>
              <div className="forecast-home-table-wrap">
                <table className="forecast-home-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>NI gap $MM</th>
                      <th>NII</th>
                      <th>Fees</th>
                      <th>Expense</th>
                      <th>Inputter comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consolidation.topUnitMovers.map((mover) => (
                      <tr key={mover.unit}>
                        <td><strong>{mover.unit}</strong></td>
                        <td className={`forecast-mono ${(mover.niDollarsMM || 0) >= 0 ? 'pos' : 'neg'}`}>
                          {(mover.niDollarsMM || 0) >= 0 ? '+' : ''}{(mover.niDollarsMM || 0).toFixed(1)}
                        </td>
                        <td className="forecast-mono">{formatPct(mover.gaps?.nii)}</td>
                        <td className="forecast-mono">{formatPct(mover.gaps?.fees)}</td>
                        <td className="forecast-mono">{formatPct(mover.gaps?.expense)}</td>
                        <td className="comment-cell">{mover.comment || <span className="dim-note">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {glLensRows && (glLensRows.nir.length > 0 || glLensRows.nie.length > 0) && (
            <section className="forecast-panel">
              <div className="forecast-panel-head">
                <span className="forecast-section-code">RV_03E ● GL group lens</span>
                <h2>Which fee and expense buckets are carrying the gap ($MM annualized vs plan)</h2>
              </div>
              <div className="gl-lens-grid">
                <div>
                  <h4>Non-interest revenue buckets</h4>
                  <div className="consol-bridge">
                    {(() => {
                      const maxAbs = Math.max(...glLensRows.nir.map((row) => Math.abs(row.gapMM)), 0.1);
                      return glLensRows.nir.map((row) => (
                        <div className="consol-bridge-row compact" key={row.glGroup}>
                          <span className="consol-bridge-label">{row.glGroup}</span>
                          <span className="consol-bridge-track">
                            <span
                              className={`consol-bridge-bar ${row.gapMM >= 0 ? 'pos' : 'neg'}`}
                              style={{ width: `${(Math.abs(row.gapMM) / maxAbs) * 100}%` }}
                            />
                          </span>
                          <span className={`forecast-mono ${row.gapMM >= 0 ? 'pos' : 'neg'}`}>
                            {row.gapMM >= 0 ? '+' : ''}{row.gapMM.toFixed(1)}
                          </span>
                          <span className="consol-bridge-detail">{formatPct(row.gapPct)}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
                <div>
                  <h4>Non-interest expense buckets (overrun = red)</h4>
                  <div className="consol-bridge">
                    {(() => {
                      const maxAbs = Math.max(...glLensRows.nie.map((row) => Math.abs(row.gapMM)), 0.1);
                      return glLensRows.nie.map((row) => (
                        <div className="consol-bridge-row compact" key={row.glGroup}>
                          <span className="consol-bridge-label">{row.glGroup}</span>
                          <span className="consol-bridge-track">
                            <span
                              className={`consol-bridge-bar ${row.gapMM > 0 ? 'neg' : 'pos'}`}
                              style={{ width: `${(Math.abs(row.gapMM) / maxAbs) * 100}%` }}
                            />
                          </span>
                          <span className={`forecast-mono ${row.gapMM > 0 ? 'neg' : 'pos'}`}>
                            {row.gapMM >= 0 ? '+' : ''}{row.gapMM.toFixed(1)}
                          </span>
                          <span className="consol-bridge-detail">{formatPct(row.gapPct)}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
              <p className="forecast-submit-note">
                Company-level GL hierarchy roll-up: each bar compares the 2026 annualized actual for
                the bucket against the prior-cycle plan. Together with the division bridge above,
                this answers both "where" (division) and "what" (GL account family).
              </p>
            </section>
          )}

          {inputterLens.length > 0 && (
            <section className="forecast-panel">
              <div className="forecast-panel-head">
                <span className="forecast-section-code">RV_03F ● Inputter lens</span>
                <h2>Run-rate gap accumulated across each liaison's book of units</h2>
              </div>
              <div className="consol-bridge">
                {(() => {
                  const maxAbs = Math.max(...inputterLens.map((row) => Math.abs(row.gapMM)), 0.1);
                  return inputterLens.map((row) => (
                    <div className="consol-bridge-row" key={row.liaison}>
                      <span className="consol-bridge-label">{row.liaison}</span>
                      <span className="consol-bridge-track">
                        <span
                          className={`consol-bridge-bar ${row.gapMM >= 0 ? 'pos' : 'neg'}`}
                          style={{ width: `${(Math.abs(row.gapMM) / maxAbs) * 100}%` }}
                        />
                      </span>
                      <span className={`forecast-mono ${row.gapMM >= 0 ? 'pos' : 'neg'}`}>
                        {row.gapMM >= 0 ? '+' : ''}{row.gapMM.toFixed(1)}
                      </span>
                      <span className="consol-bridge-detail">{row.units} units</span>
                    </div>
                  ));
                })()}
              </div>
              <p className="forecast-submit-note">
                Not a scorecard — a routing aid. A concentration here tells you which liaison's
                review conversation covers the most consolidated dollars.
              </p>
            </section>
          )}

          <section className="forecast-panel">
            <div className="forecast-panel-head">
              <span className="forecast-section-code">RV_03D ● Multi-year context</span>
              <h2>Total company — earnings and balance trajectories vs plan</h2>
            </div>
            <div className="consol-chart-grid">
              <div>
                <h4>Net income ($MM)</h4>
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={companyAnnual.map((row) => ({
                      ...row,
                      actualAnnualized: row.actualNi !== null && row.actualMonths
                        ? (row.actualNi / row.actualMonths) * 12 / 1e6
                        : null,
                      priorNiMM: row.priorNi !== null ? row.priorNi / 1e6 : null,
                      budgetNiMM: row.budgetNi !== null ? row.budgetNi / 1e6 : null
                    }))}>
                      <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                      <XAxis dataKey="year" />
                      <YAxis tickFormatter={(value) => `$${Math.round(value)}MM`} width={70} />
                      <Tooltip formatter={(value) => [`$${Number(value).toFixed(1)}MM`]} />
                      <Bar dataKey="priorNiMM" name="Prior cycle plan" fill="var(--ace-chart-2, #6b7c93)" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="budgetNiMM" name="Budget FY2026" fill="var(--ace-chart-3, #9aa7b8)" radius={[3, 3, 0, 0]} />
                      <Line dataKey="actualAnnualized" name="Actual run-rate" stroke="#FFAC03" strokeWidth={2} dot />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <h4>Loan & deposit balances ($BN, EOP)</h4>
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={companyAnnual.map((row) => ({
                      year: row.year,
                      actualLoansBN: row.actualLoans !== null ? row.actualLoans / 1e9 : null,
                      priorLoansBN: row.priorLoans !== null ? row.priorLoans / 1e9 : null,
                      actualDepositsBN: row.actualDeposits !== null ? row.actualDeposits / 1e9 : null,
                      priorDepositsBN: row.priorDeposits !== null ? row.priorDeposits / 1e9 : null
                    }))}>
                      <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                      <XAxis dataKey="year" />
                      <YAxis tickFormatter={(value) => `$${Number(value).toFixed(0)}BN`} width={70} />
                      <Tooltip formatter={(value) => [`$${Number(value).toFixed(2)}BN`]} />
                      <Line dataKey="priorLoansBN" name="Loans — prior plan" stroke="var(--ace-chart-2, #6b7c93)" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                      <Line dataKey="actualLoansBN" name="Loans — actual" stroke="#FFAC03" strokeWidth={2} dot />
                      <Line dataKey="priorDepositsBN" name="Deposits — prior plan" stroke="var(--ace-chart-3, #9aa7b8)" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                      <Line dataKey="actualDepositsBN" name="Deposits — actual" stroke="var(--ace-moss, #5d8a66)" strokeWidth={2} dot />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <p className="forecast-submit-note">
              Solid lines are actuals; dashed lines are the prior-cycle plan. The balance panel shows
              whether the earnings gap is a volume story (balances off plan) or a margin story
              (balances on plan, earnings off).
            </p>
          </section>

          <section className="forecast-panel">
            <div className="forecast-panel-head">
              <span className="forecast-section-code">RV_04 ● Forecast load center</span>
              <h2>
                <UploadCloud size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                Write approved packages to the forecast cube
              </h2>
            </div>
            {approvedRows.length === 0 ? (
              <p className="forecast-submit-note">No approved packages yet. Approve from the drill-in review first.</p>
            ) : (
              <div className="forecast-home-table-wrap">
                <table className="forecast-home-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Status</th>
                      <th>Approved by</th>
                      <th>Scenario version</th>
                      <th>Result</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {approvedRows.map((submission) => (
                      <tr key={submission.id}>
                        <td><strong>{submission.division}</strong><span className="forecast-home-market"> · {submission.market}</span></td>
                        <td>
                          <span className={`forecast-status-chip ${STATUS_TONES[submission.status]}`}>
                            {STATUS_LABELS[submission.status]}
                          </span>
                        </td>
                        <td>{submission.reviewedBy || '—'}</td>
                        <td className="forecast-mono">{submission.modelSnapshot?.scenarioVersion || '—'}</td>
                        <td>
                          {loadResults[submission.id]?.status === 'error' ? (
                            <span className="neg">{loadResults[submission.id].message}</span>
                          ) : loadResults[submission.id] ? (
                            <span className="pos">
                              {loadResults[submission.id].insertedRows} rows · batch {String(loadResults[submission.id].loadBatchId || '').slice(0, 8)}
                            </span>
                          ) : submission.status === 'loaded' ? 'Loaded' : '—'}
                        </td>
                        <td>
                          {submission.status === 'approved' && (
                            <button
                              type="button"
                              className="forecast-home-action"
                              disabled={loadingUnit === submission.id}
                              onClick={() => writeApprovedUnit(submission)}
                            >
                              {loadingUnit === submission.id ? 'Writing…' : 'Write forecast'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ForecastReviewConsole;
